import {
  ReportUnits, ReportFonts, ReportPaperSize,
  ReportPageMargin, ReportLine, ReportImage,
  ReportRectangle, ReportSignature, ReportElement,
  ElementType, ReportProperties
} from '../../../_objects/report-element';
import { ReportPreviewMetadata, UpdateMetadata } from '../../../_services/offline-report-preview.service';
import { PreviewController } from './preview-controller';
import { ReportCustomElement } from './report-custom-element';
import { ReportDropDownList } from './report-dropdown-list';
import { ReportISO } from './report-iso';
import { ReportMeasurementPoint } from './report-measurement-point';
import { ReportSection } from './report-section';
import { ReportTable } from './report-table';
import {
  TemplateDefinition, TemplateText, TemplateLine,
  TemplateImage, TemplateRectangle, TemplateSummary,
  TemplateDataPlate, TemplateSignature, TemplateTable,
  TemplatePageSection, TemplateMeasurementPoint, TemplateVisualEvents,
  TemplateSection, TemplateIso, TemplateDropDownList, TemplateCustomElements,
  ElementLocationInSection, TemplateTableOffline
} from './report-template';
import { ReportVisualEvents } from './report-visual-events';
import { Location, FromTo } from '../../../_objects/report-element';
import { ReportSummary } from './report-summary';
import { ReportDataPlate } from './report-data-plate';
import { ReportText } from './report-text';
import { ReportCommonSection } from './report-common-section';
import { ReportPage } from './report-page';
import { ImageComponent, TableData } from './TableData';

export class ReportModel {
  private templateDef: TemplateDefinition;
  private units: ReportUnits;
  private fonts: ReportFonts;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private pageHeader: ReportCommonSection;
  private pageFooter: ReportCommonSection;

  private pages: { [elementId: string]: ReportPage };
  private previewPages: { [elementId: string]: ReportPage };

  private headerTexts: { [elementId: string]: ReportText };
  private headerLines: { [elementId: string]: ReportLine };
  private headerImages: { [elementId: string]: ReportImage };
  private headerRectangles: { [elementId: string]: ReportRectangle };
  private headerSummaries: { [elementId: string]: ReportSummary };
  private headerPictures: { [elementId: string]: ReportSummary };
  private headerDataPlates: { [elementId: string]: ReportDataPlate };
  private headerSignatures: { [elementId: string]: ReportSignature };
  private headerTables: { [elementId: string]: ReportTable };

  private footerTexts: { [elementId: string]: ReportText };
  private footerLines: { [elementId: string]: ReportLine };
  private footerImages: { [elementId: string]: ReportImage };
  private footerRectangles: { [elementId: string]: ReportRectangle };
  private footerSummaries: { [elementId: string]: ReportSummary };
  private footerPictures: { [elementId: string]: ReportSummary };
  private footerDataPlates: { [elementId: string]: ReportDataPlate };
  private footerSignatures: { [elementId: string]: ReportSignature };
  private footerTables: { [elementId: string]: ReportTable };

  private bodyTexts: { [elementId: string]: ReportText };
  private bodyLines: { [elementId: string]: ReportLine };
  private bodyImages: { [elementId: string]: ReportImage };
  private bodyRectangles: { [elementId: string]: ReportRectangle };
  private bodySummaries: { [elementId: string]: ReportSummary };
  private bodyPictures: { [elementId: string]: ReportSummary };
  private bodyDataPlates: { [elementId: string]: ReportDataPlate };
  private bodyMeasurementPoint: { [elementId: string]: ReportMeasurementPoint };
  private bodyVisualEvents: { [elementId: string]: ReportVisualEvents };
  private bodySignatures: { [elementId: string]: ReportSignature };
  private bodySections: { [elementId: string]: ReportSection };
  private bodyTables: { [elementId: string]: ReportTable };
  private bodyIso: { [elementId: string]: ReportISO };
  private bodyDropDownLists: { [elementId: string]: ReportDropDownList };
  private bodyCustomElements: { [elementId: string]: ReportCustomElement };

  private elementFinder: { [key: string]: ReportElement };
  private previewElementFinder: { [elementId: string]: ReportPage };

  get ElementFinder(): { [key: string]: ReportElement } {
    return this.elementFinder;
  }

  private previewController: PreviewController;

  constructor(templateDef: TemplateDefinition) {
    this.units = new ReportUnits(templateDef);
    this.fonts = new ReportFonts(templateDef, this.units);
    this.paperSize = new ReportPaperSize(templateDef);
    this.pageMargin = new ReportPageMargin(templateDef);
    this.pageHeader = new ReportCommonSection('Page Header', templateDef.header);
    this.pageFooter = new ReportCommonSection('Page Footer', templateDef.footer);
    this.templateDef = templateDef;
    this.pages = {};
    this.previewPages = {};

    this.headerTexts = {};
    this.headerLines = {};
    this.headerImages = {};
    this.headerRectangles = {};
    this.headerSummaries = {};
    this.headerPictures = {};
    this.headerDataPlates = {};
    this.headerSignatures = {};
    this.headerTables = {};

    this.bodyTexts = {};
    this.bodyLines = {};
    this.bodyImages = {};
    this.bodyRectangles = {};
    this.bodySummaries = {};
    this.bodyPictures = {};
    this.bodyDataPlates = {};
    this.bodyMeasurementPoint = {};
    this.bodyVisualEvents = {};
    this.bodySignatures = {};
    this.bodySections = {};
    this.bodyTables = {};
    this.bodyIso = {};
    this.bodyDropDownLists = {};
    this.bodyCustomElements = {};

    this.footerTexts = {};
    this.footerLines = {};
    this.footerImages = {};
    this.footerRectangles = {};
    this.footerSummaries = {};
    this.footerPictures = {};
    this.footerDataPlates = {};
    this.footerSignatures = {};
    this.footerTables = {};

    this.elementFinder = {};
    this.previewElementFinder = {};

    this.previewController = new PreviewController();

    // Header項目の初期化
    this.elementFactory<TemplateText, ReportText>(
      this.headerTexts, this.textCreator.bind(this), Location.Header,
      templateDef.header.texts
    );
    this.elementFactory<TemplateLine, ReportLine>(
      this.headerLines, this.lineCreator.bind(this), Location.Header,
      templateDef.header.lines
    );
    this.elementFactory<TemplateImage, ReportImage>(
      this.headerImages, this.imageCreator.bind(this), Location.Header,
      templateDef.header.images
    );
    this.elementFactory<TemplateRectangle, ReportRectangle>(
      this.headerRectangles, this.rectangleCreator.bind(this), Location.Header,
      templateDef.header.rectangles
    );
    if (templateDef.header.inspectionSummary) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.headerSummaries, this.summaryCreator.bind(this), Location.Header,
        [templateDef.header.inspectionSummary]
      );
    }
    if (templateDef.header.inspectionPictures) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.headerPictures, this.pictureCreator.bind(this), Location.Header,
        [templateDef.header.inspectionPictures]
      );
    }
    if (templateDef.header.dataPlates) {
      this.elementFactory<TemplateDataPlate, ReportDataPlate>(
        this.headerDataPlates, this.dataPlateCreator.bind(this), Location.Header,
        [templateDef.header.dataPlates]
      );
    }
    if (templateDef.header.signatures) {
      this.elementFactory<TemplateSignature, ReportSignature>(
        this.headerSignatures, this.signatureCreator.bind(this), Location.Header,
        templateDef.header.signatures
      );
    }
    if (templateDef.header.tables) {
      this.elementFactory<TemplateTable, ReportTable>(
        this.headerTables, this.tableCreator.bind(this), Location.Header,
        templateDef.header.tables
      );
    }

    // Footer項目の初期化
    this.elementFactory<TemplateText, ReportText>(
      this.footerTexts, this.textCreator.bind(this), Location.Footer,
      templateDef.footer.texts
    );
    this.elementFactory<TemplateLine, ReportLine>(
      this.footerLines, this.lineCreator.bind(this), Location.Footer,
      templateDef.footer.lines
    );
    this.elementFactory<TemplateImage, ReportImage>(
      this.footerImages, this.imageCreator.bind(this), Location.Footer,
      templateDef.footer.images
    );
    this.elementFactory<TemplateRectangle, ReportRectangle>(
      this.footerRectangles, this.rectangleCreator.bind(this), Location.Footer,
      templateDef.footer.rectangles
    );
    if (templateDef.footer.inspectionSummary) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.footerSummaries, this.summaryCreator.bind(this), Location.Footer,
        [templateDef.footer.inspectionSummary]
      );
    }
    if (templateDef.footer.inspectionPictures) {
      this.elementFactory<TemplateSummary, ReportSummary>(
        this.footerPictures, this.pictureCreator.bind(this), Location.Footer,
        [templateDef.footer.inspectionPictures]
      );
    }
    if (templateDef.footer.dataPlates) {
      this.elementFactory<TemplateDataPlate, ReportDataPlate>(
        this.footerDataPlates, this.dataPlateCreator.bind(this), Location.Footer,
        [templateDef.footer.dataPlates]
      );
    }
    if (templateDef.footer.signatures) {
      this.elementFactory<TemplateSignature, ReportSignature>(
        this.footerSignatures, this.signatureCreator.bind(this), Location.Footer,
        templateDef.footer.signatures
      );
    }
    if (templateDef.footer.tables) {
      this.elementFactory<TemplateTable, ReportTable>(
        this.footerTables, this.tableCreator.bind(this), Location.Footer,
        templateDef.footer.tables
      );
    }

    // Pageの初期化
    this.elementFactory<TemplatePageSection, ReportPage>(
      this.pages, this.pageCreator.bind(this), Location.Page,
      templateDef.bodies
    );

    // Page上の項目の初期化
    this.initPageItems(templateDef, this.pages);
  }

  public getPageIds(): Array<string> {
    return this.previewController.getPageIds();
  }

  public getIds(pageId: string, location: Location, type: ElementType): Array<string> {
    return this.previewController.getIds(pageId, location, type);
  }

  public previewElementsIds(pageId: string): string[] {
    return this.previewController.previewElementsIds(pageId);
  }

  public removeAllPage() {
    return this.previewController.removeAllPage();
  }

  public getElement(id: string): ReportElement {
    return this.elementFinder[id];
  }
  public getElements(ids: string[]): ReportElement[] {
    const rtnArray = new Array<ReportElement>();
    for (const id of ids) {
      rtnArray.push(this.getElement(id));
    }
    return rtnArray;
  }

  public getHeaderTexts(): { [elementId: string]: ReportText } {
    return this.headerTexts;
  }

  public getHeaderLines(): { [elementId: string]: ReportLine } {
    return this.headerLines;
  }

  public getHeaderImages(): { [elementId: string]: ReportImage } {
    return this.headerImages;
  }

  public getHeaderRectangles(): { [elementId: string]: ReportRectangle } {
    return this.headerRectangles;
  }

  public getHeaderSignatures(): { [elementId: string]: ReportSignature } {
    return this.headerSignatures;
  }

  public getHeaderTables(): { [elementId: string]: ReportTable } {
    return this.headerTables;
  }

  public getFooterTexts(): { [elementId: string]: ReportText } {
    return this.footerTexts;
  }

  public getFooterLines(): { [elementId: string]: ReportLine } {
    return this.footerLines;
  }

  public getFooterImages(): { [elementId: string]: ReportImage } {
    return this.footerImages;
  }

  public getFooterRectangles(): { [elementId: string]: ReportRectangle } {
    return this.footerRectangles;
  }

  public getFooterSignatures(): { [elementId: string]: ReportSignature } {
    return this.footerSignatures;
  }

  public getFooterTables(): { [elementId: string]: ReportTable } {
    return this.footerTables;
  }

  public getBodyTexts(): { [elementId: string]: ReportText } {
    return this.bodyTexts;
  }

  public getBodyLines(): { [elementId: string]: ReportLine } {
    return this.bodyLines;
  }

  public getBodyImages(): { [elementId: string]: ReportImage } {
    return this.bodyImages;
  }

  public getBodyRectangles(): { [elementId: string]: ReportRectangle } {
    return this.bodyRectangles;
  }

  public getBodyMeasurementPoints(): { [elementId: string]: ReportMeasurementPoint } {
    return this.bodyMeasurementPoint;
  }

  public getBodyVisualEvents(): { [elementId: string]: ReportVisualEvents } {
    return this.bodyVisualEvents;
  }

  public getBodySignatures(): { [elementId: string]: ReportSignature } {
    return this.bodySignatures;
  }

  public getBodySections(): { [elementId: string]: ReportSection } {
    return this.bodySections;
  }

  public getBodyTables(): { [elementId: string]: ReportTable } {
    return this.bodyTables;
  }

  public getBodyInteractiveIso(): { [elementId: string]: ReportISO } {
    return this.bodyIso;
  }

  public getBodyDropDownLists(): { [elementId: string]: ReportDropDownList } {
    return this.bodyDropDownLists;
  }

  public getBodyCustomElements(): { [elementId: string]: ReportCustomElement } {
    return this.bodyCustomElements;
  }

  public getPage(id: string): ReportPage {
    return this.pages[id];
  }

  public getPreviewPage(id: string): ReportPage {
    return this.previewPages[id];
  }
  public getPages(ids: string[]): ReportPage[] {
    const rtnArray = new Array<ReportPage>();
    for (const id of ids) {
      rtnArray.push(this.getPage(id));
    }
    return rtnArray;
  }

  public getText(id: string): ReportText {
    return this.elementFinder[id] as ReportText;
  }
  public getTexts(ids: string[]): ReportText[] {
    const rtnArray = new Array<ReportText>();
    for (const id of ids) {
      rtnArray.push(this.getText(id));
    }
    return rtnArray;
  }

  public getLine(id: string): ReportLine {
    return this.elementFinder[id] as ReportLine;
  }
  public getLines(ids: string[]): ReportLine[] {
    const rtnArray = new Array<ReportLine>();
    for (const id of ids) {
      rtnArray.push(this.getLine(id));
    }
    return rtnArray;
  }

  public getImage(id: string): ReportImage {
    return this.elementFinder[id] as ReportImage;
  }
  public getImages(ids: string[]): ReportImage[] {
    const rtnArray = new Array<ReportImage>();
    for (const id of ids) {
      rtnArray.push(this.getImage(id));
    }
    return rtnArray;
  }

  public getRectangle(id: string): ReportRectangle {
    return this.elementFinder[id] as ReportRectangle;
  }
  public getRectangles(ids: string[]): ReportRectangle[] {
    const rtnArray = new Array<ReportRectangle>();
    for (const id of ids) {
      rtnArray.push(this.getRectangle(id));
    }
    return rtnArray;
  }

  public getSummary(id: string): ReportSummary {
    return this.elementFinder[id] as ReportSummary;
  }
  public getSummaries(ids: string[]): ReportSummary[] {
    const rtnArray = new Array<ReportSummary>();
    for (const id of ids) {
      rtnArray.push(this.getSummary(id));
    }
    return rtnArray;
  }

  public getDataPlate(id: string): ReportDataPlate {
    return this.elementFinder[id] as ReportDataPlate;
  }
  public getDataPlates(ids: string[]): ReportDataPlate[] {
    const rtnArray = new Array<ReportDataPlate>();
    for (const id of ids) {
      rtnArray.push(this.getDataPlate(id));
    }
    return rtnArray;
  }

  public getMeasurementPoint(id: string): ReportMeasurementPoint {
    return this.elementFinder[id] as ReportMeasurementPoint;
  }
  public getMeasurementPoints(ids: string[]): ReportMeasurementPoint[] {
    const rtnArray = new Array<ReportMeasurementPoint>();
    for (const id of ids) {
      rtnArray.push(this.getMeasurementPoint(id));
    }
    return rtnArray;
  }

  public getVisualEvent(id: string): ReportVisualEvents {
    return this.elementFinder[id] as ReportVisualEvents;
  }
  public getVisualEvents(ids: string[]): ReportVisualEvents[] {
    const rtnArray = new Array<ReportVisualEvents>();
    for (const id of ids) {
      rtnArray.push(this.getVisualEvent(id));
    }
    return rtnArray;
  }

  public getSignature(id: string): ReportSignature {
    return this.elementFinder[id] as ReportSignature;
  }
  public getSignatures(ids: string[]): ReportSignature[] {
    const rtnArray = new Array<ReportSignature>();
    for (const id of ids) {
      rtnArray.push(this.getSignature(id));
    }
    return rtnArray;
  }

  public getSection(id: string): ReportSection {
    return this.elementFinder[id] as ReportSection;
  }
  public getSections(ids: string[]): ReportSection[] {
    const rtnArray = new Array<ReportSection>();
    for (const id of ids) {
      rtnArray.push(this.getSection(id));
    }
    return rtnArray;
  }

  public getTable(id: string): ReportTable {
    return this.elementFinder[id] as ReportTable;
  }
  public getTables(ids: string[]): ReportTable[] {
    const rtnArray = new Array<ReportTable>();
    for (const id of ids) {
      rtnArray.push(this.getTable(id));
    }
    return rtnArray;
  }

  public getInterActiveIso(id: string): ReportISO {
    return this.elementFinder[id] as ReportISO;
  }
  public getInterActiveIsos(ids: string[]): ReportISO[] {
    const rtnArray = new Array<ReportISO>();
    for (const id of ids) {
      rtnArray.push(this.getInterActiveIso(id));
    }
    return rtnArray;
  }

  public getDropDownList(id: string): ReportDropDownList {
    return this.elementFinder[id] as ReportDropDownList;
  }
  public getDropDownLists(ids: string[]): ReportDropDownList[] {
    return ids.map((id) => this.getDropDownList(id));
  }

  public getCustomElement(id: string): ReportCustomElement {
    return this.elementFinder[id] as ReportCustomElement;
  }
  public getCustomElements(ids: string[]): ReportCustomElement[] {
    const rtnArray = new Array<ReportCustomElement>();
    for (const id of ids) {
      rtnArray.push(this.getCustomElement(id));
    }
    return rtnArray;
  }

  public addPage(viewPageId?: string): void {

    const createdIds = this.elementFactory<TemplatePageSection, ReportPage>(
      this.pages, this.pageCreator.bind(this), Location.Page
    );

    for (const pageId of createdIds) {
      this.pages[pageId].isSelected = true;
      this.previewController.addPage(pageId, viewPageId);

      this.previewController.redrawHeader(
        pageId, this.pages[pageId].hasHeader,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates),
        Object.keys(this.headerSignatures),
        Object.keys(this.headerTables)
      );

      this.previewController.redrawFooter(
        pageId, this.pages[pageId].hasFooter,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates),
        Object.keys(this.footerSignatures),
        Object.keys(this.footerTables)
      );
    }
  }

  public addCustomPage(): void {
    // making default to 0 for custom element page
    this.templateDef.header.height = 0;
    this.templateDef.footer.height = 0;
    this.templateDef.pageMarginTop = this.templateDef.pageMarginBottom
      = this.templateDef.pageMarginLeft = this.templateDef.pageMarginRight = 0;
    this.pageMargin = new ReportPageMargin(this.templateDef);
    this.pageHeader = new ReportCommonSection('Page Header', this.templateDef.header);
    this.pageFooter = new ReportCommonSection('Page Footer', this.templateDef.footer);
    const createdIds = this.elementFactory<TemplatePageSection, ReportPage>(
      this.pages, this.pageCreator.bind(this), Location.Page
    );

    for (const pageId of createdIds) {
      this.pages[pageId].isSelected = true;
      this.previewController.addPage(pageId);
    }
  }

  public beginDrawMode() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() !== ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
      this.elementFinder[elementId].isSelectable = false;
    }
    for (const pageId of Object.keys(this.pages)) {
      this.pages[pageId].isDrawMode = true;
    }
  }

  public endDrawMode() {
    for (const elementId of Object.keys(this.elementFinder)) {
      this.elementFinder[elementId].isSelectable = true;
    }
    for (const pageId of Object.keys(this.pages)) {
      this.pages[pageId].isDrawMode = false;
    }
  }

  public drawElement(
    location: Location,
    type: ElementType,
    metadata: ReportPreviewMetadata,
    drawPageId?: string
  ) {

    const instanceMap = this.getInstanceMap(location, type);
    let createdIds: string[] = [];

    switch (type) {
      case ElementType.Text:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateText, ReportText>(
            instanceMap, this.textCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateText, ReportText>(
            instanceMap, this.textCreator.bind(this), location
          );
        }
        break;
      case ElementType.Line:
        if (metadata.heightPx >= 0 || metadata.widthPx >= 0) {
          if (drawPageId) {
            createdIds = this.elementFactoryOnPage<TemplateLine, ReportLine>(
              instanceMap, this.lineCreator.bind(this), location,
              this.pages[drawPageId].getIndex(), drawPageId
            );
          } else {
            createdIds = this.elementFactory<TemplateLine, ReportLine>(
              instanceMap, this.lineCreator.bind(this), location
            );
          }
        }
        break;
      case ElementType.Image:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateImage, ReportImage>(
            instanceMap, this.imageCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateImage, ReportImage>(
            instanceMap, this.imageCreator.bind(this), location
          );
        }
        break;
      case ElementType.Rectangle:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
            instanceMap, this.rectangleCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateRectangle, ReportRectangle>(
            instanceMap, this.rectangleCreator.bind(this), location
          );
        }
        break;

      case ElementType.MeasurementPoint:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateMeasurementPoint, ReportMeasurementPoint>(
            instanceMap, this.measurementPointCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateMeasurementPoint, ReportMeasurementPoint>(
            instanceMap, this.measurementPointCreator.bind(this), location
          );
        }
        break;

      case ElementType.VisualEvents:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateVisualEvents, ReportVisualEvents>(
            instanceMap, this.visualEventsCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateVisualEvents, ReportVisualEvents>(
            instanceMap, this.visualEventsCreator.bind(this), location
          );
        }
        break;

      case ElementType.Signature:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateSignature, ReportSignature>(
            instanceMap, this.signatureCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateSignature, ReportSignature>(
            instanceMap, this.signatureCreator.bind(this), location
          );
        }
        break;

      case ElementType.Section:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateSection, ReportSection>(
            instanceMap, this.sectionCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateSection, ReportSection>(
            instanceMap, this.sectionCreator.bind(this), location
          );
        }
        break;

      case ElementType.Table:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateTable, ReportTable>(
            instanceMap, this.tableCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateTable, ReportTable>(
            instanceMap, this.tableCreator.bind(this), location
          );
        }
        break;

      case ElementType.InteractiveIso:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateIso, ReportISO>(
            instanceMap, this.interactiveIsoCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateIso, ReportISO>(
            instanceMap, this.interactiveIsoCreator.bind(this), location
          );
        }
        break;

      case ElementType.DropDownList:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateDropDownList, ReportDropDownList>(
            instanceMap, this.dropDownListCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateDropDownList, ReportDropDownList>(
            instanceMap, this.dropDownListCreator.bind(this), location
          );
        }
        break;

      case ElementType.Summary:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
            instanceMap, this.summaryCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateSummary, ReportSummary>(
            instanceMap, this.summaryCreator.bind(this), location
          );
        }
        break;
      case ElementType.Picture:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
            instanceMap, this.pictureCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateSummary, ReportSummary>(
            instanceMap, this.pictureCreator.bind(this), location
          );
        }
        break;
      case ElementType.DataPlate:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateDataPlate, ReportDataPlate>(
            instanceMap, this.dataPlateCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateSummary, ReportDataPlate>(
            instanceMap, this.dataPlateCreator.bind(this), location
          );
        }
        break;

      case ElementType.CustomElement:
        if (drawPageId) {
          createdIds = this.elementFactoryOnPage<TemplateCustomElements, ReportCustomElement>(
            instanceMap, this.customElementsCreator.bind(this), location,
            this.pages[drawPageId].getIndex(), drawPageId
          );
        } else {
          createdIds = this.elementFactory<TemplateCustomElements, ReportCustomElement>(
            instanceMap, this.customElementsCreator.bind(this), location
          );
        }
        break;

    }

    createdIds.forEach((elementId: string) => {

      const pageIds = Object.keys(this.pages);
      const element = this.elementFinder[elementId];

      element.setDrawData(metadata);
      element.isSelected = true;

      switch (location) {
        case Location.Header:
          for (const pageId of pageIds) {
            this.previewController.redrawHeader(
              pageId, this.pages[pageId].hasHeader,
              Object.keys(this.headerTexts),
              Object.keys(this.headerLines),
              Object.keys(this.headerImages),
              Object.keys(this.headerRectangles),
              Object.keys(this.headerSummaries),
              Object.keys(this.headerPictures),
              Object.keys(this.headerDataPlates),
              Object.keys(this.headerSignatures),
              Object.keys(this.headerTables)
            );
          }
          break;
        case Location.Footer:
          for (const pageId of pageIds) {
            this.previewController.redrawFooter(
              pageId, this.pages[pageId].hasFooter,
              Object.keys(this.footerTexts),
              Object.keys(this.footerLines),
              Object.keys(this.footerImages),
              Object.keys(this.footerRectangles),
              Object.keys(this.footerSummaries),
              Object.keys(this.footerPictures),
              Object.keys(this.footerDataPlates),
              Object.keys(this.footerSignatures),
              Object.keys(this.footerTables)
            );
          }
          break;
        case Location.Body:
          this.previewController.draw(drawPageId, location, type, elementId);
          break;
      }
    });
  }

  public dropElement(elementId: string, type: ElementType, dropMetadata: ReportPreviewMetadata, fromTo: FromTo) {

    const fromInstanceMap = this.getInstanceMap(fromTo.from.location, type);
    const toInstanceMap = this.getInstanceMap(fromTo.to.location, type);
    const dropElement = fromInstanceMap[elementId];
    const dropPageId = fromTo.to.pageId;
    const dropPageIndex = this.elementFinder[dropPageId].getIndex();
    const pageIds = Object.keys(this.pages);

    if (
      fromTo.from.location === fromTo.to.location &&
      fromTo.from.pageId === fromTo.to.pageId
    ) {
      // fromとtoが同じ＝ドラッグアンドドロップでの移動先が同じ場合は
      // instanceMapの更新とpreviewControllerの更新は行わない
    } else {
      // fromとtoが異なる＝ドラッグアンドドロップでの移動先が別の場合は
      // ドラッグアンドドロップによる移動に併せてinstancemapの中身の更新と
      // previewControllerが管理する要素の順番を担保している配列を更新する
      delete fromInstanceMap[elementId];
      toInstanceMap[elementId] = dropElement;
      this.previewController.move(elementId, type, fromTo);

      // 移動する要素のロケーション情報を更新する
      // HeaderかFooterに移動した場合は、Locationのみ指定して更新する（＝pageIndex, pageIdはundefinedで上書きされる＝消える）
      // Bodyに移動した場合は、Location＋pageIndex＋pageIdを指定して更新する
      if (fromTo.to.location === Location.Header || fromTo.to.location === Location.Footer) {
        dropElement.setLocation(fromTo.to.location);
      } else {
        dropElement.setLocation(fromTo.to.location, dropPageId, dropPageIndex);
      }

      // 要素自体が持つindex値を更新する
      // 1ページ上に配置する要素の数や並び順を担保しているのはpreviewControllerなので、
      // previewControllerが保持しているID配列を基に採番し直す
      const fromIds = this.previewController.getIds(fromTo.from.pageId, fromTo.from.location, type);
      for (let fromIdIndex = 0, fromIdMax = fromIds.length; fromIdIndex < fromIdMax; fromIdIndex++) {
        fromInstanceMap[fromIds[fromIdIndex]].setIndex(fromIdIndex);
      }
      const toIds = this.previewController.getIds(fromTo.to.pageId, fromTo.to.location, type);
      for (let toIdIndex = 0, toIdMax = toIds.length; toIdIndex < toIdMax; toIdIndex++) {
        toInstanceMap[toIds[toIdIndex]].setIndex(toIdIndex);
      }
    }

    // 移動する要素のtopとleftを更新する
    dropElement.setDropData(dropMetadata);

    // 移動元がHeader(Header項目が減った)、または移動先がHeader（Header項目が増えた）場合、全ページのHeader項目を再描画する
    if (fromTo.from.location === Location.Header || fromTo.to.location === Location.Header) {
      for (const pageId of pageIds) {
        this.previewController.redrawHeader(
          pageId, this.pages[pageId].hasHeader,
          Object.keys(this.headerTexts),
          Object.keys(this.headerLines),
          Object.keys(this.headerImages),
          Object.keys(this.headerRectangles),
          Object.keys(this.headerSummaries),
          Object.keys(this.headerPictures),
          Object.keys(this.headerDataPlates),
          Object.keys(this.headerSignatures),
          Object.keys(this.headerTables)
        );
      }
    }

    // 移動元がFooter(Footer項目が減った)、または移動先がFooter（Footer項目が増えた）場合、全ページのFooter項目を再描画する
    if (fromTo.from.location === Location.Footer || fromTo.to.location === Location.Footer) {
      for (const pageId of pageIds) {
        this.previewController.redrawFooter(
          pageId, this.pages[pageId].hasFooter,
          Object.keys(this.footerTexts),
          Object.keys(this.footerLines),
          Object.keys(this.footerImages),
          Object.keys(this.footerRectangles),
          Object.keys(this.footerSummaries),
          Object.keys(this.footerPictures),
          Object.keys(this.footerDataPlates),
          Object.keys(this.footerSignatures),
          Object.keys(this.footerTables)
        );
      }
    }
  }

  public resizeElement(elementId: string, resizeOffset: ReportPreviewMetadata, markerPointH: string, markerPointV: string) {
    const resizeElement = this.elementFinder[elementId];

    const resizeMetadata = {
      topPx: resizeElement.getTopPx(),
      leftPx: resizeElement.getLeftPx(),
      widthPx: resizeElement.getWidthPx(),
      heightPx: resizeElement.getHeightPx()
    };

    // console.log('resize metadata(before): %s', JSON.stringify(resizeMetadata, undefined, 4));
    switch (markerPointH) {
      case 'left':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            break;
          case 'bottom':
            resizeMetadata.leftPx = resizeMetadata.leftPx + resizeOffset.leftPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + (resizeOffset.widthPx * -1);
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;

      case 'center':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            // 要素の上下左右中央にマーカーは配置していないため処理なし
            break;
          case 'bottom':
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;

      case 'right':
        switch (markerPointV) {
          case 'top':
            resizeMetadata.topPx = resizeMetadata.topPx + resizeOffset.topPx;
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + (resizeOffset.heightPx * -1);
            break;
          case 'middle':
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            break;
          case 'bottom':
            resizeMetadata.widthPx = resizeMetadata.widthPx + resizeOffset.widthPx;
            resizeMetadata.heightPx = resizeMetadata.heightPx + resizeOffset.heightPx;
            break;
        }
        break;
    }

    // console.log('resize metadata(after): %s', JSON.stringify(resizeMetadata, undefined, 4));

    resizeElement.setResizeData(resizeMetadata);
  }

  public updateCommonProperties(metadata: UpdateMetadata) {
    const properties = this.getCommonProperties();
    for (const props of properties) {
      if (props.getId() === metadata.id) {
        props.updateProperties(metadata);
        break;
      }
    }
  }

  public updateElementProperties(metadata: UpdateMetadata) {
    const element = this.elementFinder[metadata.id];

    element.updateProperties(metadata);

    if (element.getElementType() === ElementType.Page && metadata.name === 'hasHeader') {
      this.previewController.redrawHeader(
        metadata.id, metadata.value as boolean,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates),
        Object.keys(this.headerSignatures),
        Object.keys(this.headerTables)
      );
    }
    if (element.getElementType() === ElementType.Page && metadata.name === 'hasFooter') {
      this.previewController.redrawFooter(
        metadata.id, metadata.value as boolean,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates),
        Object.keys(this.footerSignatures),
        Object.keys(this.footerTables)
      );
    }
  }

  public selectElement(targetId: string, isCtrl: boolean) {
    const selectedElement = this.elementFinder[targetId];
    if (isCtrl) {
      selectedElement.isSelected = !selectedElement.isSelected;
    } else {
      this.deselectAllCore();
      selectedElement.isSelected = true;
    }
  }
  public changePage(targetId: string) {
    const selectedElement = this.elementFinder[targetId];
    this.deselectAllPage();
    if (selectedElement) {
      selectedElement.isSelected = true;
    }
  }

  public deselectAll(isCtrl: boolean) {
    if (!isCtrl) {
      this.deselectAllCore();
    }
  }

  public copySelected(
    location: Location,
    type: ElementType,
    baseDef: Array<any>,
    drawPageId?: string
  ) {

    const instanceMap = this.getInstanceMap(location, type);
    let createdIds: string[] = [];

    switch (type) {

      case ElementType.Text:
        createdIds = this.elementFactoryOnPage<TemplateText, ReportText>(
          instanceMap, this.textCreator.bind(this), location,
          this.pages[drawPageId].getIndex(), drawPageId, baseDef
        );
        this.previewController.draw(drawPageId, location, ElementType.Text, createdIds);
        break;

      case ElementType.Line:
        createdIds = this.elementFactoryOnPage<TemplateLine, ReportLine>(
          instanceMap, this.lineCreator.bind(this), location,
          this.pages[drawPageId].getIndex(), drawPageId, baseDef
        );
        this.previewController.draw(drawPageId, location, ElementType.Line, createdIds);
        break;

      case ElementType.Image:
        createdIds = this.elementFactoryOnPage<TemplateImage, ReportImage>(
          instanceMap, this.imageCreator.bind(this), location,
          this.pages[drawPageId].getIndex(), drawPageId, baseDef
        );
        this.previewController.draw(drawPageId, location, ElementType.Image, createdIds);
        break;

      case ElementType.Rectangle:
        createdIds = this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
          instanceMap, this.rectangleCreator.bind(this), location,
          this.pages[drawPageId].getIndex(), drawPageId, baseDef
        );
        this.previewController.draw(drawPageId, location, ElementType.Rectangle, createdIds);
        break;

      case ElementType.DropDownList:
        createdIds = this.elementFactoryOnPage<TemplateDropDownList, ReportDropDownList>(
          instanceMap, this.dropDownListCreator.bind(this), location,
          this.pages[drawPageId].getIndex(), drawPageId, baseDef
        );
        this.previewController.draw(drawPageId, location, ElementType.DropDownList, createdIds);
        break;
    }
  }

  public deleteElementsById(elementId: string, location: Location): void {
    if (this.elementFinder[elementId].getPageId()) {
      this.previewController.remove(this.elementFinder[elementId].getPageId(),
        location, this.elementFinder[elementId].getId());
      this.removeBodyElement(elementId);
    } else {
      const pageIds = Object.keys(this.pages);
      if (location === Location.Header) {
        this.removeHeaderElement(elementId);
        for (const pageId of pageIds) {
          this.previewController.redrawHeader(
            pageId, this.pages[pageId].hasHeader,
            Object.keys(this.headerTexts),
            Object.keys(this.headerLines),
            Object.keys(this.headerImages),
            Object.keys(this.headerRectangles),
            Object.keys(this.headerSummaries),
            Object.keys(this.headerPictures),
            Object.keys(this.headerDataPlates),
            Object.keys(this.headerSignatures),
            Object.keys(this.headerTables)
          );
        }
      }

      if (location === Location.Footer) {
        this.removeFooterElement(elementId);
        for (const pageId of pageIds) {
          this.previewController.redrawFooter(
            pageId, this.pages[pageId].hasFooter,
            Object.keys(this.footerTexts),
            Object.keys(this.footerLines),
            Object.keys(this.footerImages),
            Object.keys(this.footerRectangles),
            Object.keys(this.footerSummaries),
            Object.keys(this.footerPictures),
            Object.keys(this.footerDataPlates),
            Object.keys(this.footerSignatures),
            Object.keys(this.footerTables)
          );
        }
      }
    }
  }

  public deleteSelected(): void {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].isSelected && this.elementFinder[elementId].getElementType() !== ElementType.Page) {
        const location = this.elementFinder[elementId].getLocation();
        if (this.elementFinder[elementId].getPageId()) {
          this.previewController.remove(this.elementFinder[elementId].getPageId(),
            location, this.elementFinder[elementId].getId());
          this.removeBodyElement(elementId);
        } else {
          const pageIds = Object.keys(this.pages);
          // Header項目が減った場合、全ページのHeader項目を再描画する
          if (location === Location.Header) {
            this.removeHeaderElement(elementId);
            for (const pageId of pageIds) {
              this.previewController.redrawHeader(
                pageId, this.pages[pageId].hasHeader,
                Object.keys(this.headerTexts),
                Object.keys(this.headerLines),
                Object.keys(this.headerImages),
                Object.keys(this.headerRectangles),
                Object.keys(this.headerSummaries),
                Object.keys(this.headerPictures),
                Object.keys(this.headerDataPlates),
                Object.keys(this.headerSignatures),
                Object.keys(this.headerTables)
              );
            }
          }

          // Footer項目が減った場合、全ページのFooter項目を再描画する
          if (location === Location.Footer) {
            this.removeFooterElement(elementId);
            for (const pageId of pageIds) {
              this.previewController.redrawFooter(
                pageId, this.pages[pageId].hasFooter,
                Object.keys(this.footerTexts),
                Object.keys(this.footerLines),
                Object.keys(this.footerImages),
                Object.keys(this.footerRectangles),
                Object.keys(this.footerSummaries),
                Object.keys(this.footerPictures),
                Object.keys(this.footerDataPlates),
                Object.keys(this.footerSignatures),
                Object.keys(this.footerTables)
              );
            }
          }
        }
      }
    }
  }
  public removeHeaderElement(elementId: string): void {
    this.remove(this.headerTexts, elementId);
    this.remove(this.headerLines, elementId);
    this.remove(this.headerImages, elementId);
    this.remove(this.headerRectangles, elementId);
    this.remove(this.headerSignatures, elementId);
  }
  public removeBodyElement(elementId: string): void {
    this.remove(this.bodyTexts, elementId);
    this.remove(this.bodyLines, elementId);
    this.remove(this.bodyImages, elementId);
    this.remove(this.bodyRectangles, elementId);
    this.remove(this.bodyMeasurementPoint, elementId);
    this.remove(this.bodyVisualEvents, elementId);
    this.remove(this.bodySignatures, elementId);
    this.remove(this.bodyIso, elementId);
    this.remove(this.bodyDropDownLists, elementId);
  }
  public removeFooterElement(elementId: string): void {
    this.remove(this.footerTexts, elementId);
    this.remove(this.footerLines, elementId);
    this.remove(this.footerImages, elementId);
    this.remove(this.footerRectangles, elementId);
    this.remove(this.footerSignatures, elementId);
  }

  public remove(target, elementId): void {
    if (elementId in target) {
      delete target[elementId];
    }
  }

  public getCommonProperties(): ReportProperties[] {
    return [
      // this.units,
      this.fonts,
      this.paperSize,
      this.pageMargin,
      this.pageHeader,
      this.pageFooter
    ];
  }

  public getPaperHeightPx(): number {
    return this.paperSize.getHeightPx();
  }

  public getSelectedElementProperties(): ReportProperties[] {
    const rtnArray = new Array<ReportProperties>();
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].isSelected) {
        rtnArray.push(this.elementFinder[elementId]);
      }
    }
    return rtnArray;
  }

  private deselectAllCore() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() !== ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
    }
  }
  private deselectAllPage() {
    for (const elementId of Object.keys(this.elementFinder)) {
      if (this.elementFinder[elementId].getElementType() === ElementType.Page) {
        this.elementFinder[elementId].isSelected = false;
      }
    }
  }

  private elementFactory<T, R extends ReportElement>(
    dest: { [elementId: string]: R },
    creator: (index: number, templateDef?: T) => R,
    location: Location,
    src?: Array<T>
  ): Array<string> {

    const createdIds = new Array<string>();

    // インスタンスの生成＋指定された格納先へ格納
    if (!src) {

      // destのプロパティ配列の要素数＝次に設定するプロパティのindexと同等なので
      // -1などせずそのまま与える
      const myIndex = Object.keys(dest).length;
      const instance = creator(myIndex);
      const elementId = instance.getId();

      createdIds.push(elementId);
      instance.setLocation(location);

      dest[elementId] = instance;
      this.elementFinder[elementId] = instance;

    } else {

      for (let i = 0, max = src.length; i < max; i++) {

        const instance = creator(i, src[i]);
        const elementId = instance.getId();

        createdIds.push(elementId);
        instance.setLocation(location);
        dest[elementId] = instance;
        this.elementFinder[elementId] = instance;
      }
    }

    return createdIds;
  }

  public previewPageFactory(summaryTable: TableData[][], pictureTable: TableData[][],
    dataPlates: ImageComponent[][]): Array<string> {
    // Preview
    this.previewController = new PreviewController();
    this.bodyTexts = {};
    this.bodyLines = {};
    this.bodyImages = {};
    this.bodyRectangles = {};
    this.bodySummaries = {};
    this.bodyPictures = {};
    this.bodyDataPlates = {};
    this.bodyMeasurementPoint = {};
    this.bodyVisualEvents = {};
    this.bodySignatures = {};
    this.bodySections = {};
    this.bodyTables = {};
    this.bodyIso = {};
    this.bodyDropDownLists = {};
    this.bodyCustomElements = {};

    const src = this.templateDef.bodies;
    const location = Location.Page;
    const createdIds = new Array<string>();
    let addPageCount = 0;
    let backToSummaryId = '';

    // インスタンスの生成＋指定された格納先へ格納
    for (let i = 0, max = src.length; i < max; i++) {

      const template = JSON.parse(JSON.stringify(src[i])) as TemplatePageSection;
      const reportPage = this.pageCreator(addPageCount, template);
      addPageCount++;
      const elementId = reportPage.getId();

      if (reportPage.hasInspectionSummary()) {
        if (summaryTable.length > 0) {
          reportPage.summaryDataList = summaryTable[0];
          reportPage.summaryFirst = true;
          backToSummaryId = reportPage.getInspectionSummary().table.id;
          createdIds.push(elementId);
          reportPage.setLocation(location);
          this.previewPages[elementId] = reportPage;
          this.previewElementFinder[elementId] = reportPage;
          this.setPageData(template, reportPage);
          this.setPreviewController(reportPage);
        }

        if (summaryTable.length > 1) {
          for (let j = 1; j < summaryTable.length; j++) {
            const appPage = this.pageCreator(addPageCount, template);
            addPageCount++;
            const addElementId = appPage.generateId();
            appPage.summaryDataList = summaryTable[j];

            createdIds.push(elementId);
            appPage.setLocation(location);
            this.previewPages[addElementId] = appPage;
            this.previewElementFinder[addElementId] = appPage;
            this.setPageData(template, appPage);
            this.setPreviewController(appPage);
          }
        }
      } else if (reportPage.hasInspectionPictures()) {
        if (pictureTable.length > 0) {
          reportPage.pictureDataList = pictureTable[0];
          reportPage.pictureFirst = true;
          createdIds.push(elementId);
          reportPage.setLocation(location);
          this.previewPages[elementId] = reportPage;
          this.previewElementFinder[elementId] = reportPage;
          this.setPageData(template, reportPage);
          this.setPreviewController(reportPage);
        }

        if (pictureTable.length > 1) {
          for (let j = 1; j < pictureTable.length; j++) {
            const appPage = this.pageCreator(addPageCount, template);
            addPageCount++;
            const addElementId = appPage.generateId();
            appPage.pictureDataList = pictureTable[j];

            createdIds.push(elementId);
            appPage.setLocation(location);
            this.previewPages[addElementId] = appPage;
            this.previewElementFinder[addElementId] = appPage;
            this.setPageData(template, appPage);
            this.setPreviewController(appPage);
          }
        }
      } else if (reportPage.hasInspectionDataPlates()) {

        reportPage.dataPlateDataList = dataPlates.length > 0 ? dataPlates[0] : null;
        reportPage.dataPlateFirst = true;
        createdIds.push(elementId);
        reportPage.setLocation(location);
        this.previewPages[elementId] = reportPage;
        this.previewElementFinder[elementId] = reportPage;
        this.setPageData(template, reportPage);
        this.setPreviewController(reportPage);

        if (dataPlates.length > 1) {
          for (let j = 1; j < dataPlates.length; j++) {
            const appPage = this.pageCreator(addPageCount, template);
            addPageCount++;
            const addElementId = appPage.generateId();
            appPage.dataPlateDataList = dataPlates[j];

            createdIds.push(elementId);
            appPage.setLocation(location);
            this.previewPages[addElementId] = appPage;
            this.previewElementFinder[addElementId] = appPage;
            this.setPageData(template, appPage);
            this.setPreviewController(appPage);
          }
        }
      } else {
        createdIds.push(elementId);
        reportPage.setLocation(location);
        this.previewPages[elementId] = reportPage;
        this.previewElementFinder[elementId] = reportPage;
        this.setPageData(template, reportPage);
        this.setPreviewController(reportPage);
      }

    }

    Object.keys(this.previewPages)
      .forEach(key => this.previewPages[key].backToSummaryId = backToSummaryId);
    return createdIds;
  }

  initPageItems(templateDef: TemplateDefinition, pages: { [elementId: string]: ReportPage }) {
    // Page上の項目の初期化
    for (const pageId of Object.keys(pages)) {
      const page = pages[pageId];
      const pageIndex = page.getIndex();
      const body = templateDef.bodies[pageIndex];
      this.elementFactoryOnPage<TemplateText, ReportText>(
        this.bodyTexts, this.textCreator.bind(this), Location.Body, pageIndex, pageId,
        body.texts
      );
      this.elementFactoryOnPage<TemplateLine, ReportLine>(
        this.bodyLines, this.lineCreator.bind(this), Location.Body, pageIndex, pageId,
        body.lines,
      );
      this.elementFactoryOnPage<TemplateImage, ReportImage>(
        this.bodyImages, this.imageCreator.bind(this), Location.Body, pageIndex, pageId,
        body.images
      );
      this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
        this.bodyRectangles, this.rectangleCreator.bind(this), Location.Body, pageIndex, pageId,
        body.rectangles
      );

      if (body.inspectionSummary) {
        this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
          this.bodySummaries, this.summaryCreator.bind(this), Location.Body, pageIndex, pageId,
          [body.inspectionSummary]
        );
      }
      if (body.inspectionPictures) {
        this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
          this.bodyPictures, this.pictureCreator.bind(this), Location.Body, pageIndex, pageId,
          [body.inspectionPictures]
        );
      }
      if (body.dataPlates) {
        this.elementFactoryOnPage<TemplateDataPlate, ReportDataPlate>(
          this.bodyDataPlates, this.dataPlateCreator.bind(this), Location.Body, pageIndex, pageId,
          [body.dataPlates]
        );
      }
      if (body.measurementPoints) {
        this.elementFactoryOnPage<TemplateMeasurementPoint, ReportMeasurementPoint>(
          this.bodyMeasurementPoint, this.measurementPointCreator.bind(this), Location.Body, pageIndex, pageId,
          body.measurementPoints
        );
      }
      if (body.visualEvents) {
        this.elementFactoryOnPage<TemplateVisualEvents, ReportVisualEvents>(
          this.bodyVisualEvents, this.visualEventsCreator.bind(this), Location.Body, pageIndex, pageId,
          body.visualEvents
        );
      }
      if (body.signatures) {
        this.elementFactoryOnPage<TemplateSignature, ReportSignature>(
          this.bodySignatures, this.signatureCreator.bind(this), Location.Body, pageIndex, pageId,
          body.signatures
        );
      }
      if (body.sections) {
        this.elementFactoryOnPage<TemplateSection, ReportSection>(
          this.bodySections, this.sectionCreator.bind(this), Location.Body, pageIndex, pageId,
          body.sections
        );
      }
      if (body.tables) {
        this.elementFactoryOnPage<TemplateTableOffline, ReportTable>(
          this.bodyTables, this.tableCreator.bind(this), Location.Body, pageIndex, pageId,
          body.tables
        );
      }
      if (body.interactiveIso) {
        this.elementFactoryOnPage<TemplateIso, ReportISO>(
          this.bodyIso, this.interactiveIsoCreator.bind(this), Location.Body, pageIndex, pageId,
          body.interactiveIso
        );
      }
      if (body.dropDownLists) {
        this.elementFactoryOnPage<TemplateDropDownList, ReportDropDownList>(
          this.bodyDropDownLists, this.dropDownListCreator.bind(this), Location.Body, pageIndex, pageId,
          body.dropDownLists
        );
      }
      if (body.customElements) {
        this.elementFactoryOnPage<TemplateCustomElements, ReportCustomElement>(
          this.bodyCustomElements, this.customElementsCreator.bind(this), Location.Body, pageIndex, pageId,
          body.customElements
        );
      }
    }
    for (const pageId of Object.keys(pages)) {
      const page = pages[pageId];
      this.previewController.addPage(pageId);

      this.previewController.redrawHeader(
        pageId, page.hasHeader,
        Object.keys(this.headerTexts),
        Object.keys(this.headerLines),
        Object.keys(this.headerImages),
        Object.keys(this.headerRectangles),
        Object.keys(this.headerSummaries),
        Object.keys(this.headerPictures),
        Object.keys(this.headerDataPlates),
        Object.keys(this.headerSignatures),
        Object.keys(this.headerTables)
      );

      this.previewController.redrawFooter(
        pageId, page.hasFooter,
        Object.keys(this.footerTexts),
        Object.keys(this.footerLines),
        Object.keys(this.footerImages),
        Object.keys(this.footerRectangles),
        Object.keys(this.footerSummaries),
        Object.keys(this.footerPictures),
        Object.keys(this.footerDataPlates),
        Object.keys(this.footerSignatures),
        Object.keys(this.footerTables)
      );

      this.previewController.redrawBody(
        pageId,
        Object.keys(this.instanceMapFilter(this.bodyTexts, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyLines, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyImages, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyRectangles, pageId)),
        Object.keys(this.instanceMapFilter(this.bodySummaries, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyPictures, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyDataPlates, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyMeasurementPoint, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyVisualEvents, pageId)),
        Object.keys(this.instanceMapFilter(this.bodySignatures, pageId)),
        Object.keys(this.instanceMapFilter(this.bodySections, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyTables, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyIso, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyDropDownLists, pageId)),
        Object.keys(this.instanceMapFilter(this.bodyCustomElements, pageId))
      );
    }
  }

  setPageData(body: TemplatePageSection, page: ReportPage) {
    const pageIndex = page.getIndex();
    const pageId = page.getId();
    this.elementFactoryOnPage<TemplateText, ReportText>(
      this.bodyTexts, this.textCreator.bind(this), Location.Body, pageIndex, pageId,
      body.texts
    );
    this.elementFactoryOnPage<TemplateLine, ReportLine>(
      this.bodyLines, this.lineCreator.bind(this), Location.Body, pageIndex, pageId,
      body.lines,
    );
    this.elementFactoryOnPage<TemplateImage, ReportImage>(
      this.bodyImages, this.imageCreator.bind(this), Location.Body, pageIndex, pageId,
      body.images
    );
    this.elementFactoryOnPage<TemplateRectangle, ReportRectangle>(
      this.bodyRectangles, this.rectangleCreator.bind(this), Location.Body, pageIndex, pageId,
      body.rectangles
    );

    if (body.inspectionSummary) {
      this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
        this.bodySummaries, this.summaryCreator.bind(this), Location.Body, pageIndex, pageId,
        [body.inspectionSummary]
      );
    }
    if (body.inspectionPictures) {
      this.elementFactoryOnPage<TemplateSummary, ReportSummary>(
        this.bodyPictures, this.pictureCreator.bind(this), Location.Body, pageIndex, pageId,
        [body.inspectionPictures]
      );
    }
    if (body.dataPlates) {
      this.elementFactoryOnPage<TemplateDataPlate, ReportDataPlate>(
        this.bodyDataPlates, this.dataPlateCreator.bind(this), Location.Body, pageIndex, pageId,
        [body.dataPlates]
      );
    }
    if (body.measurementPoints) {
      this.elementFactoryOnPage<TemplateMeasurementPoint, ReportMeasurementPoint>(
        this.bodyMeasurementPoint, this.measurementPointCreator.bind(this), Location.Body, pageIndex, pageId,
        body.measurementPoints
      );
    }
    if (body.visualEvents) {
      this.elementFactoryOnPage<TemplateVisualEvents, ReportVisualEvents>(
        this.bodyVisualEvents, this.visualEventsCreator.bind(this), Location.Body, pageIndex, pageId,
        body.visualEvents
      );
    }
    if (body.signatures) {
      this.elementFactoryOnPage<TemplateSignature, ReportSignature>(
        this.bodySignatures, this.signatureCreator.bind(this), Location.Body, pageIndex, pageId,
        body.signatures
      );
    }
    if (body.sections) {
      this.elementFactoryOnPage<TemplateSection, ReportSection>(
        this.bodySections, this.sectionCreator.bind(this), Location.Body, pageIndex, pageId,
        body.sections
      );
    }
    if (body.tables) {
      this.elementFactoryOnPage<TemplateTableOffline, ReportTable>(
        this.bodyTables, this.tableCreator.bind(this), Location.Body, pageIndex, pageId,
        body.tables
      );
    }
    if (body.interactiveIso) {
      this.elementFactoryOnPage<TemplateIso, ReportISO>(
        this.bodyIso, this.interactiveIsoCreator.bind(this), Location.Body, pageIndex, pageId,
        body.interactiveIso
      );
    }
    if (body.dropDownLists) {
      this.elementFactoryOnPage<TemplateDropDownList, ReportDropDownList>(
        this.bodyDropDownLists, this.dropDownListCreator.bind(this), Location.Body, pageIndex, pageId,
        body.dropDownLists
      );
    }
    if (body.customElements) {
      this.elementFactoryOnPage<TemplateCustomElements, ReportCustomElement>(
        this.bodyCustomElements, this.customElementsCreator.bind(this), Location.Body, pageIndex, pageId,
        body.customElements
      );
    }
  }

  setPreviewController(page: ReportPage) {
    const pageId = page.getId();
    this.previewController.addPage(pageId);

    this.previewController.redrawHeader(
      pageId, page.hasHeader,
      Object.keys(this.headerTexts),
      Object.keys(this.headerLines),
      Object.keys(this.headerImages),
      Object.keys(this.headerRectangles),
      Object.keys(this.headerSummaries),
      Object.keys(this.headerPictures),
      Object.keys(this.headerDataPlates),
      Object.keys(this.headerSignatures),
      Object.keys(this.headerTables)
    );

    this.previewController.redrawFooter(
      pageId, page.hasFooter,
      Object.keys(this.footerTexts),
      Object.keys(this.footerLines),
      Object.keys(this.footerImages),
      Object.keys(this.footerRectangles),
      Object.keys(this.footerSummaries),
      Object.keys(this.footerPictures),
      Object.keys(this.footerDataPlates),
      Object.keys(this.footerSignatures),
      Object.keys(this.footerTables)
    );

    this.previewController.redrawBody(
      pageId,
      Object.keys(this.instanceMapFilter(this.bodyTexts, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyLines, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyImages, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyRectangles, pageId)),
      Object.keys(this.instanceMapFilter(this.bodySummaries, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyPictures, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyDataPlates, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyMeasurementPoint, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyVisualEvents, pageId)),
      Object.keys(this.instanceMapFilter(this.bodySignatures, pageId)),
      Object.keys(this.instanceMapFilter(this.bodySections, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyTables, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyIso, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyDropDownLists, pageId)),
      Object.keys(this.instanceMapFilter(this.bodyCustomElements, pageId))
    );
  }

  private elementFactoryOnPage<T, R extends ReportElement>(
    dest: { [elementId: string]: R },
    creator: (index: number, templateDef?: T) => R,
    location: Location,
    pageIndex: number,
    pageId: string,
    src?: Array<T>,
  ): Array<string> {

    const createdIds = new Array<string>();

    if (!src) {
      const myIndex = Object.keys(this.instanceMapFilter(dest, pageId)).length;
      const instance = creator(myIndex);
      const elementId = instance.getId();

      createdIds.push(elementId);
      instance.setLocation(location, pageId, pageIndex);

      dest[elementId] = instance;
      this.elementFinder[elementId] = instance;

    } else {
      // インスタンスの生成＋指定された格納先へ格納
      for (let i = 0, max = src.length; i < max; i++) {

        const instance = creator(i, src[i]);
        const elementId = instance.getId();

        createdIds.push(elementId);
        instance.setLocation(location, pageId, pageIndex);

        dest[elementId] = instance;
        this.elementFinder[elementId] = instance;
      }
    }

    return createdIds;
  }

  private pageCreator(index: number, templatePage?: TemplatePageSection): ReportPage {
    return new ReportPage(
      index, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templatePage
    );
  }

  private textCreator(index: number, templateText?: TemplateText): ReportText {
    return new ReportText(index, this.units, this.fonts, templateText);
  }
  private lineCreator(index: number, templateLine?: TemplateLine): ReportLine {
    return new ReportLine(index, this.units, templateLine);
  }
  private imageCreator(index: number, templateImage?: TemplateImage): ReportImage {
    return new ReportImage(index, this.units, this.fonts, templateImage);
  }
  private rectangleCreator(index: number, templateRectangle?: TemplateRectangle): ReportRectangle {
    return new ReportRectangle(index, this.units, templateRectangle);
  }
  private measurementPointCreator(index: number, templateTable?: TemplateMeasurementPoint): ReportMeasurementPoint {
    return new ReportMeasurementPoint(ElementType.MeasurementPoint, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateTable);
  }
  private visualEventsCreator(index: number, templateVisualEvents?: TemplateVisualEvents): ReportVisualEvents {
    return new ReportVisualEvents(ElementType.VisualEvents, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateVisualEvents);
  }
  private customElementsCreator(index: number, templateVisualEvents?: TemplateCustomElements): ReportCustomElement {
    return new ReportCustomElement(ElementType.CustomElement, index, this.units, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateVisualEvents);
  }
  private summaryCreator(index: number, templateSummary?: TemplateSummary): ReportSummary {
    return new ReportSummary(ElementType.Summary, index, this.units, this.fonts, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateSummary);
  }
  private pictureCreator(index: number, templatePicture?: TemplateSummary): ReportSummary {
    return new ReportSummary(ElementType.Picture, index, this.units, this.fonts, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templatePicture);
  }
  private dataPlateCreator(index: number, templateDataPlate?: TemplateDataPlate): ReportDataPlate {
    return new ReportDataPlate(ElementType.DataPlate, index, this.units, this.fonts, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateDataPlate);
  }
  private signatureCreator(index: number, templateSignature?: TemplateSignature): ReportSignature {
    return new ReportSignature(index, this.units, this.fonts, templateSignature);
  }
  private sectionCreator(index: number, templateSection?: TemplateSection): ReportSection {
    return new ReportSection(index, this.units, templateSection);
  }
  private tableCreator(index: number, templateTable?: TemplateTableOffline): ReportTable {
    return new ReportTable(index, this.units, this.fonts, this.pageHeader, this.pageFooter,
      this.paperSize, this.pageMargin, templateTable);
  }
  private interactiveIsoCreator(index: number, templateIso?: TemplateIso): ReportISO {
    return new ReportISO(index, this.units, templateIso);
  }
  private dropDownListCreator(index: number, templateDropDownList?: TemplateDropDownList): ReportDropDownList {
    return new ReportDropDownList(index, this.units, templateDropDownList);
  }

  private instanceMapFilter<R extends ReportElement>(src: { [elementId: string]: R }, pageId: string): { [elementId: string]: R } {
    const rtn: { [elementId: string]: R } = {};
    for (const elementId of Object.keys(src)) {
      if (src[elementId].getPageId() === pageId) {
        rtn[elementId] = src[elementId];
      }
    }
    return rtn;
  }

  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportText };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportLine };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportImage };
  // private getInstanceMap(location: Location, type: ElementType): { [elementId: string]: ReportRectangle };
  private getInstanceMap(location: Location, type: ElementType): any {
    switch (location) {
      case Location.Header:
        switch (type) {
          case ElementType.Text:
            return this.headerTexts;
          case ElementType.Line:
            return this.headerLines;
          case ElementType.Image:
            return this.headerImages;
          case ElementType.Rectangle:
            return this.headerRectangles;
          case ElementType.Summary:
            return this.headerSummaries;
          case ElementType.Picture:
            return this.headerPictures;
          case ElementType.DataPlate:
            return this.headerDataPlates;
          case ElementType.Signature:
            return this.headerSignatures;
          case ElementType.Table:
            return this.headerTables;
          default:
            return null;
        }
      case Location.Footer:
        switch (type) {
          case ElementType.Text:
            return this.footerTexts;
          case ElementType.Line:
            return this.footerLines;
          case ElementType.Image:
            return this.footerImages;
          case ElementType.Rectangle:
            return this.footerRectangles;
          case ElementType.Summary:
            return this.footerSummaries;
          case ElementType.Picture:
            return this.footerPictures;
          case ElementType.DataPlate:
            return this.footerDataPlates;
          case ElementType.Signature:
            return this.footerSignatures;
          case ElementType.Table:
            return this.footerTables;
          default:
            return null;
        }
      case Location.Body:
        switch (type) {
          case ElementType.Text:
            return this.bodyTexts;
          case ElementType.Line:
            return this.bodyLines;
          case ElementType.Image:
            return this.bodyImages;
          case ElementType.Rectangle:
            return this.bodyRectangles;
          case ElementType.Summary:
            return this.bodySummaries;
          case ElementType.Picture:
            return this.bodyPictures;
          case ElementType.DataPlate:
            return this.bodyDataPlates;
          case ElementType.MeasurementPoint:
            return this.bodyMeasurementPoint;
          case ElementType.VisualEvents:
            return this.bodyVisualEvents;
          case ElementType.Signature:
            return this.bodySignatures;
          case ElementType.Section:
            return this.bodySections;
          case ElementType.Table:
            return this.bodyTables;
          case ElementType.InteractiveIso:
            return this.bodyIso;
          case ElementType.DropDownList:
            return this.bodyDropDownLists;
          case ElementType.CustomElement:
            return this.bodyCustomElements;
          default:
            return null;
        }
    }
  }

  public alignElementsWithSection(pageId: string): void {
    this.getIds(pageId, Location.Body, ElementType.Section).forEach((sectionId) => {
      const section = this.elementFinder[sectionId] as ReportSection;
      const position = {
        xStart: section.getTemplateJSON().x,
        xEnd: section.getTemplateJSON().x + section.getTemplateJSON().width,
        yStart: section.getTemplateJSON().y,
        yEnd: section.getTemplateJSON().y + section.getTemplateJSON().height
      };

      this.filterElementsInSection(pageId, ElementType.Text, position, section);
      this.filterElementsInSection(pageId, ElementType.Line, position, section);
      this.filterElementsInSection(pageId, ElementType.Image, position, section);
      this.filterElementsInSection(pageId, ElementType.Rectangle, position, section);
      this.filterElementsInSection(pageId, ElementType.Summary, position, section);
      this.filterElementsInSection(pageId, ElementType.Picture, position, section);
      this.filterElementsInSection(pageId, ElementType.DataPlate, position, section);
      this.filterElementsInSection(pageId, ElementType.MeasurementPoint, position, section);
      this.filterElementsInSection(pageId, ElementType.VisualEvents, position, section);
      this.filterElementsInSection(pageId, ElementType.Signature, position, section);
      this.filterElementsInSection(pageId, ElementType.Table, position, section);
      this.filterElementsInSection(pageId, ElementType.InteractiveIso, position, section);
      this.filterElementsInSection(pageId, ElementType.DropDownList, position, section);
    });
  }

  private filterElementsInSection<R extends ReportElement>(pageId: string, elType: ElementType, position: any, section: ReportSection)
    : { [elementId: string]: R } {
    const rtn: { [elementId: string]: R } = {};
    this.getIds(pageId, Location.Body, elType).forEach((itemId) => {
      const item = this.elementFinder[itemId] as any;
      if (item.getTemplateJSON().x >= position.xStart && item.getTemplateJSON().x <= position.xEnd &&
        item.getTemplateJSON().y >= position.yStart && item.getTemplateJSON().y <= position.yEnd) {
        const location: ElementLocationInSection = {
          x: item.units.concatenate('sizeUnit', (item.getTemplateJSON().x - position.xStart) as number),
          y: item.units.concatenate('sizeUnit', (item.getTemplateJSON().y - position.yStart) as number)
        };
        item.setSectionInfo(section.getTemplateJSON().id, section.stageId, location);
      } else if (item.getTemplateJSON().sectionId === section.getId()) {
        item.setSectionInfo('', '', undefined);
      }
    });
    return rtn;
  }

  public updateStageTagForSection(sectionId: string): void {
    const section = this.elementFinder[sectionId] as ReportSection;
    const pageId = section.getPageId();
    const position = {
      xStart: section.getTemplateJSON().x,
      xEnd: section.getTemplateJSON().x + section.getTemplateJSON().width,
      yStart: section.getTemplateJSON().y,
      yEnd: section.getTemplateJSON().y + section.getTemplateJSON().height
    };

    this.filterElementsInSection(pageId, ElementType.Text, position, section);
    this.filterElementsInSection(pageId, ElementType.Line, position, section);
    this.filterElementsInSection(pageId, ElementType.Image, position, section);
    this.filterElementsInSection(pageId, ElementType.Rectangle, position, section);
    this.filterElementsInSection(pageId, ElementType.Summary, position, section);
    this.filterElementsInSection(pageId, ElementType.Picture, position, section);
    this.filterElementsInSection(pageId, ElementType.DataPlate, position, section);
    this.filterElementsInSection(pageId, ElementType.MeasurementPoint, position, section);
    this.filterElementsInSection(pageId, ElementType.VisualEvents, position, section);
    this.filterElementsInSection(pageId, ElementType.Signature, position, section);
    this.filterElementsInSection(pageId, ElementType.InteractiveIso, position, section);
    this.filterElementsInSection(pageId, ElementType.DropDownList, position, section);
  }

  public getReportText(metaId: string, location: string): ReportText {
    let reportText: ReportText;
    if (location === 'header') {
      const keys = Object.keys(this.headerTexts);
      keys.some(key => {
        const headerTexts = this.headerTexts[key];
        if (headerTexts.getMetadataId() === metaId) {
          reportText = headerTexts;
          return true;
        }
      });
    } else if (location === 'bodies') {
      const keys = Object.keys(this.bodyTexts);
      keys.some(key => {
        const bodyTexts = this.bodyTexts[key];
        if (bodyTexts.getMetadataId() === metaId) {
          reportText = bodyTexts;
          return true;
        }
      });
    } else if (location === 'footer') {
      const keys = Object.keys(this.footerTexts);
      keys.some(key => {
        const footerTexts = this.footerTexts[key];
        if (footerTexts.getMetadataId() === metaId) {
          reportText = footerTexts;
          return true;
        }
      });
    }
    return reportText;
  }
}
